export const AVAILABILITY_CARD_CSS = /* css */ `
  .lk-availability-cards {
    /* The one place to change facility card width (bookable, connected,
       listed, and the listed stack column). Figma is 335px; courtsapp.com
       cards run 285-336px and the ~285px look reads better in the chat. */
    --lk-facility-card-width: 285px;
    display: flex;
    /* Each card keeps its Figma height (instant ~403px, connected ~320px,
       listed ~112px); stretching shorter cards left a block of white space. */
    align-items: flex-start;
    gap: 14px;
    overflow-x: auto;
    padding: 8px 15px 48px;
    margin: 0 -15px -28px;
    scrollbar-width: none;
  }
  .lk-availability-cards::-webkit-scrollbar { display: none; }

  /* ── Shared facility card shell (Figma cards, HEY-495) ──
     No overflow clip on the root: the discount ribbon hangs 3px past the left
     edge. The photo and the body round their own corners instead. */
  .lk-availability-card {
    position: relative;
    flex: 0 0 auto;
    width: var(--lk-facility-card-width);
    border-radius: 14px;
    background: var(--wg-bg);
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.04), 0 4px 4px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: var(--wg-card-fg);
    transition: transform 0.15s ease-out;
    animation: lk-card-in 0.4s ease backwards;
  }
  .lk-availability-card:nth-child(2) { animation-delay: 0.1s; }
  .lk-availability-card:nth-child(3) { animation-delay: 0.2s; }
  .lk-availability-card:nth-child(4) { animation-delay: 0.3s; }
  .lk-availability-card:hover {
    transform: scale(1.02);
  }

  /* ── Photo ── */
  /* 178px on every card (Figma draws connected at 163px) so photos and names
     line up when instant and connected cards sit side by side. */
  .lk-availability-card-media {
    position: relative;
    height: 178px;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: 14px 14px 0 0;
    background-color: var(--wg-image-placeholder-bg);
  }
  .lk-availability-card-media--bookable {
    height: 178px;
  }
  .lk-availability-card-image {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    background-color: var(--wg-image-placeholder-bg);
  }
  .lk-availability-card-instant {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 8px 0;
    border-radius: 12px 12px 0 0;
    background: rgba(255, 255, 255, 0.8);
    -webkit-backdrop-filter: blur(3px);
    backdrop-filter: blur(3px);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.13px;
    color: var(--wg-card-fg);
  }
  .lk-availability-card-instant-icon {
    width: 22px;
    height: 22px;
    padding: 4px;
    border-radius: 999px;
    background: var(--wg-cta-bg);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .lk-availability-card-instant-icon svg {
    width: 14px;
    height: 14px;
    transform: rotate(-1.79deg);
  }
  .lk-availability-card-rating {
    position: absolute;
    left: 10px;
    bottom: 10px;
    height: 24px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.55);
    font-size: 12px;
    font-weight: 600;
    line-height: normal;
    color: #fff;
  }
  .lk-availability-card-star {
    width: 11px;
    height: 11px;
    flex: 0 0 auto;
  }
  .lk-availability-card-star svg {
    display: block;
    width: 10.46px;
    height: 9.95px;
    margin: 0 auto;
  }

  /* ── Discount ribbon (lime tab, top-left of the photo) ── */
  .lk-availability-card-ribbon {
    position: absolute;
    top: 48px;
    left: -3px;
    width: 84px;
    height: 30px;
    display: flex;
    align-items: center;
    padding-left: 7px;
    color: var(--wg-cta-bg);
    pointer-events: none;
  }
  .lk-availability-card-ribbon-shape {
    position: absolute;
    inset: 0;
  }
  .lk-availability-card-ribbon-shape svg { display: block; width: 100%; height: 100%; }
  .lk-availability-card-ribbon-text {
    position: relative;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: -0.32px;
    white-space: nowrap;
    color: var(--wg-card-fg);
  }
  .lk-availability-card-ribbon-fold {
    position: absolute;
    left: 0;
    top: 30px;
    width: 4px;
    height: 4px;
    color: var(--wg-card-fg);
  }
  .lk-availability-card-ribbon-fold svg { display: block; width: 100%; height: 100%; }

  /* ── Body ── */
  .lk-availability-card-body {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    border: 1px solid #F0F0F0;
    border-top: none;
    border-radius: 0 0 14px 14px;
    background: var(--wg-bg);
    color: var(--wg-card-fg);
  }
  .lk-availability-card-top {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .lk-availability-card-name {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: -0.32px;
    color: var(--wg-card-fg);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .lk-availability-card-location {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    line-height: 1.3;
    font-weight: 400;
    letter-spacing: -0.13px;
    color: var(--wg-card-fg);
    white-space: nowrap;
  }
  .lk-availability-card-location strong {
    font-weight: 600;
  }
  .lk-availability-card-place {
    color: var(--wg-card-fg-muted);
  }
  .lk-availability-card-icon {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    color: var(--wg-card-fg-muted);
  }
  .lk-availability-card-icon svg { display: block; width: 100%; height: 100%; }

  /* ── Time slot tiles ── */
  .lk-availability-card-slots {
    display: flex;
    gap: 4px;
  }
  .lk-availability-slot {
    flex: 0 1 100px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    border-radius: 12px;
  }
  .lk-availability-slot-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 4px 6px;
    background-color: var(--wg-accent);
    border-radius: 12px 12px 0 0;
    color: #fff;
    transition: background-color 0.15s ease-out;
  }
  .lk-availability-slot:hover .lk-availability-slot-head {
    background-color: var(--wg-accent-dark);
  }
  .lk-availability-slot--discounted .lk-availability-slot-head {
    background-color: var(--wg-cta-bg);
    color: var(--wg-card-fg);
  }
  .lk-availability-slot--discounted:hover .lk-availability-slot-head {
    background-color: var(--wg-cta-bg-hover);
  }
  .lk-availability-slot-day {
    font-size: 12px;
    font-weight: 500;
    line-height: 12px;
    white-space: nowrap;
    text-transform: uppercase;
  }
  .lk-availability-slot-time {
    font-size: 14px;
    font-weight: 600;
    line-height: 14px;
    letter-spacing: -0.14px;
    white-space: nowrap;
  }
  .lk-availability-slot-body {
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px 6px 7px;
    background: var(--wg-bg);
    border: 1px solid var(--wg-border);
    border-top: none;
    border-radius: 0 0 12px 12px;
    text-align: center;
  }
  .lk-availability-slot-was {
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    color: var(--wg-card-fg-muted);
    text-decoration: line-through;
    white-space: nowrap;
  }
  .lk-availability-slot-price {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: -0.14px;
    color: var(--wg-card-fg);
    white-space: nowrap;
  }
  .lk-availability-slot-surface {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    letter-spacing: 0.13px;
    color: var(--wg-card-fg-muted);
    max-width: 100%;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  /* ── Open play card: "OPEN PLAY · PICKLEBALL" label above the name, and the
     level (the tile's surface line) wraps in full instead of clamping. ── */
  .lk-availability-card-eyebrow {
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: 0.44px;
    text-transform: uppercase;
    color: var(--wg-accent-dark);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .lk-availability-slot-surface--wrap {
    display: block;
    overflow: visible;
    -webkit-line-clamp: unset;
    overflow-wrap: break-word;
    word-break: normal;
    hyphens: auto;
  }

  /* ── "More availability" / "More info" link, right-aligned under the body ── */
  .lk-availability-card-cta {
    align-self: flex-end;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.13px;
    color: var(--wg-card-fg);
    white-space: nowrap;
  }
  .lk-availability-card-arrow {
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
    color: #343330;
  }
  .lk-availability-card-arrow svg { display: block; width: 100%; height: 100%; }
  .lk-availability-card:hover .lk-availability-card-cta {
    opacity: 0.75;
  }

  /* ── Listed stack: one carousel column of short listed cards, top-aligned ── */
  .lk-listed-stack {
    flex: 0 0 auto;
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: var(--lk-facility-card-width);
    animation: lk-card-in 0.4s ease backwards;
  }
  .lk-listed-stack > .lk-availability-card {
    animation: none;
  }

  /* ── Listed club card (no photo, no booking) ── */
  .lk-listed-card {
    gap: 6px;
    padding: 14px;
    border: 1px solid #F0F0F0;
    border-radius: 12px;
  }
  .lk-listed-card-cta {
    margin-top: 2px;
    padding: 6px 10px;
  }

  /* ── Connected club card (books on its own site; no slots) ──
     Matches the CourtsApp app's ConnectedCardFooter. */
  .lk-connected-card:hover {
    transform: none;
  }
  .lk-connected-card-photo-link {
    display: block;
    color: inherit;
    text-decoration: none;
  }
  .lk-connected-card-panel {
    flex: 1 1 auto;
    gap: 12px;
    padding: 14px 14px 20px;
  }
  .lk-connected-card-link {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    text-decoration: none;
    color: inherit;
  }
  .lk-connected-card-link:hover .lk-availability-card-name {
    text-decoration: underline;
  }
  .lk-connected-card-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .lk-connected-card-chip {
    padding: 6px 10px;
    border-radius: 999px;
    background: #E6EBE9;
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    color: var(--wg-card-fg);
  }
  .lk-connected-card-out {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }
  .lk-connected-card-out svg { display: block; width: 100%; height: 100%; }
  .lk-connected-card-book {
    height: 40px;
    border-radius: 999px;
    background: rgba(38, 209, 151, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: -0.168px;
    color: var(--wg-accent-dark);
    text-decoration: none;
    transition: filter 0.15s ease-out;
  }
  .lk-connected-card-book:hover {
    filter: brightness(0.95);
  }
  .lk-connected-card-links {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    height: 30px;
  }
  .lk-connected-card-more,
  .lk-connected-card-visit {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.13px;
    text-decoration: underline;
    text-underline-position: from-font;
    white-space: nowrap;
  }
  .lk-connected-card-more {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--wg-card-fg);
  }
  .lk-connected-card-visit {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 16px;
    color: var(--wg-accent-dark);
  }
  .lk-connected-card-more:hover,
  .lk-connected-card-visit:hover {
    opacity: 0.75;
  }
`;
